<template>
  <div class="upload-creative">
    <b-form
      @submit.prevent="handleSubmit"
      @keydown.alt.85.prevent="focusUploadButton"
    >
    <b-row>
        <!-- Left Column -->
        <b-col lg="8">
          <!-- Unified Upload Form Card -->
          <b-card class="unified-form-card">
            <!-- Upload Configuration Section -->
            <div class="form-section">
              <div class="section-header">
                <h6 class="mb-0">
                  <feather-icon
                    icon="SettingsIcon"
                    size="16"
                    class="mr-2"
                  />
                  Upload Configuration
                </h6>
              </div>
              <div class="section-body">
                <b-row>
                  <!-- Advertising ID - Full width on top -->
                  <b-col md="12">
                    <b-form-group
                      label="Advertising ID"
                      label-for="advertiser-id"
                      class="compact-form-group advertiser-section"
                    >
                      <div class="advertiser-input-group">
                        <!-- Toggle between manual input and dropdown -->
                        <div class="input-mode-toggle mb-2">
                          <b-form-radio-group
                            v-model="advertiserInputMode"
                            size="sm"
                            :options="advertiserInputModeOptions"
                            buttons
                            button-variant="outline-secondary"
                            class="compact-toggle-buttons"
                            @change="handleInputModeChange"
                          />
                        </div>

                        <!-- Manual input mode (default) -->
                        <div
                          v-if="advertiserInputMode === 'manual'"
                          class="d-flex align-items-center"
                        >
                          <b-form-input
                            v-model="uploadForm.advertiserId"
                            id="advertiser-id-manual"
                            size="sm"
                            type="text"
                            placeholder="Enter advertising ID manually..."
                            class="advertiser-input-field flex-grow-1"
                            @input="handleAdvertiserManualInput"
                          />
                          <b-button
                            v-if="uploadForm.advertiserId"
                            size="sm"
                            variant="outline-primary"
                            class="ml-2"
                            :disabled="savingDefault"
                            @click="saveAsDefaultAdvertiser"
                          >
                            <b-icon-bookmark />
                          </b-button>
                        </div>

                        <!-- Dropdown mode -->
                        <b-form-select
                          v-else
                          v-model="uploadForm.advertiserId"
                          id="advertiser-id"
                          size="sm"
                          :options="advertiserOptions"
                          class="advertiser-input-field"
                          @change="handleAdvertiserChange"
                        >
                          <template #first>
                            <b-form-select-option
                              :value="null"
                              disabled
                            >
                              Select from existing accounts
                            </b-form-select-option>
                          </template>
                        </b-form-select>
                      </div>
                      <b-form-text class="compact-help-text">
                        {{ advertiserInputMode === 'manual' ? 'Enter a custom advertising ID' : 'Choose from your existing ad accounts' }}
                      </b-form-text>
                    </b-form-group>
                  </b-col>
                </b-row>
                <!-- Creative Type - Full width -->
                <b-row>
                  <b-col md="12">
                    <b-form-group
                      label="Creative Type"
                      label-for="creative-type"
                      class="compact-form-group"
                    >
                      <div class="creative-type-toggle">
                        <b-form-radio-group
                          v-model="uploadForm.type"
                          id="creative-type"
                          :options="creativeTypeOptions"
                          buttons
                          button-variant="outline-primary"
                          size="sm"
                          @change="handleTypeChange"
                        />
                      </div>
                    </b-form-group>
                  </b-col>
                </b-row>
                <!-- Upload Method - Full width -->
                <b-row>
                  <b-col md="12">
                    <b-form-group
                      label="Upload Method"
                      label-for="upload-method"
                      class="compact-form-group upload-method-group"
                    >
                      <b-form-select
                        v-model="uploadForm.uploadMethod"
                        id="upload-method"
                        size="sm"
                        class="upload-method-select"
                        :options="availableUploadMethods"
                        @change="handleMethodChange"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
            </div>

            <!-- Upload Source Section -->
            <div class="form-section">
              <div class="section-header">
                <h6 class="mb-0">
                  <feather-icon
                    icon="UploadIcon"
                    size="16"
                    class="mr-2"
                  />
                  Upload Source
                </h6>
              </div>
              <div class="section-body">
                <!-- File Upload Section -->
                <div v-if="uploadForm.uploadMethod === 'UPLOAD_BY_FILE'">
                  <b-form-group
                    :label="uploadForm.type === 'video' ? 'Video File' : 'Image File'"
                    :label-for="`${uploadForm.type}-file`"
                    class="compact-form-group"
                  >
                    <!-- File Requirements Info Box -->
                    <div class="file-requirements-box mb-2">
                      <div class="requirements-title">
                        <feather-icon
                          icon="InfoIcon"
                          size="14"
                          class="mr-1"
                        />
                        <strong>{{ uploadForm.type === 'video' ? 'Video' : 'Image' }} Requirements:</strong>
                      </div>
                      <ul class="requirements-list">
                        <li v-if="uploadForm.type === 'video'">
                          <strong>Formats:</strong> MP4, MOV, MPEG, AVI
                        </li>
                        <li v-else>
                          <strong>Formats:</strong> JPG, JPEG, PNG
                        </li>
                        <li>
                          <strong>Max Size:</strong> {{ uploadForm.type === 'video' ? '500MB' : '50MB' }}
                        </li>
                        <li v-if="uploadForm.type === 'video'">
                          <strong>Aspect Ratio:</strong> 9:16 (vertical) or 16:9 (horizontal)
                        </li>
                        <li v-else>
                          <strong>Aspect Ratio:</strong> 1:1 (square) or 9:16 (vertical)
                        </li>
                        <li v-if="uploadForm.type === 'video'">
                          <strong>Recommended:</strong> 1080x1920px (TikTok format)
                        </li>
                        <li v-else>
                          <strong>Recommended:</strong> 1200x1200px or 1080x1920px
                        </li>
                      </ul>
                    </div>

                    <b-form-file
                      v-model="uploadForm.file"
                      ref="fileInput"
                      :id="`${uploadForm.type}-file`"
                      size="sm"
                      :accept="fileAcceptTypes"
                      :placeholder="`Choose ${uploadForm.type} file...`"
                      @input="handleFileInput"
                    />
                    <div
                      v-if="!isFileValid && fileValidationErrors.length"
                      class="text-danger mt-2"
                    >
                      <small
                        v-for="error in fileValidationErrors"
                        :key="error"
                        class="d-block"
                      >
                        {{ error }}
                      </small>
                    </div>
                  </b-form-group>
                </div>

                <!-- URL Upload Section -->
                <div v-if="uploadForm.uploadMethod === 'UPLOAD_BY_URL'">
                  <b-form-group
                    :label="uploadForm.type === 'video' ? 'Video URL' : 'Image URL'"
                    :label-for="`${uploadForm.type}-url`"
                    class="compact-form-group"
                  >
                    <!-- URL Requirements Info Box -->
                    <div class="file-requirements-box mb-2">
                      <div class="requirements-title">
                        <feather-icon
                          icon="InfoIcon"
                          size="14"
                          class="mr-1"
                        />
                        <strong>{{ uploadForm.type === 'video' ? 'Video' : 'Image' }} URL Requirements:</strong>
                      </div>
                      <ul class="requirements-list">
                        <li>
                          <strong>File Size:</strong> Better within {{ uploadForm.type === 'video' ? '10MB' : '10MB' }}
                        </li>
                        <li>
                          <strong>URL Format:</strong> Valid URL that's playable in browser (e.g., http://example.com/video.mp4)
                        </li>
                        <li>
                          <strong>Encoding:</strong> URL must be properly encoded (spaces as %20)
                        </li>
                        <li>
                          <strong>Content-Type:</strong> Server must return valid media Content-Type header
                        </li>
                        <li v-if="uploadForm.type === 'video'">
                          <strong>Supported Formats:</strong> Same as file upload (MP4, MOV, MPEG, AVI)
                        </li>
                        <li v-else>
                          <strong>Supported Formats:</strong> Same as file upload (JPG, JPEG, PNG)
                        </li>
                        <li>
                          <strong>Verification:</strong> URL will be validated before processing
                        </li>
                      </ul>
                    </div>

                    <b-form-input
                      v-model="uploadForm.url"
                      :id="`${uploadForm.type}-url`"
                      type="url"
                      size="sm"
                      :placeholder="`Enter ${uploadForm.type} URL...`"
                      :state="urlValidation"
                    />
                    <b-form-text class="compact-help-text">
                      Example: https://example.com/media/{{ uploadForm.type }}.{{ uploadForm.type === 'video' ? 'mp4' : 'jpg' }}
                    </b-form-text>
                  </b-form-group>
                </div>

                <!-- File ID Upload Section -->
                <div v-if="uploadForm.uploadMethod === 'UPLOAD_BY_FILE_ID'">
                  <b-form-group
                    label="File ID"
                    label-for="file-id"
                    class="compact-form-group"
                  >
                    <b-form-input
                      v-model="uploadForm.fileId"
                      id="file-id"
                      size="sm"
                      placeholder="Enter file ID..."
                    />
                    <b-form-text class="compact-help-text">
                      Use the file ID from a previous upload or file management system
                    </b-form-text>
                  </b-form-group>
                </div>

                <!-- Video ID Upload Section -->
                <div v-if="uploadForm.uploadMethod === 'UPLOAD_BY_VIDEO_ID' && uploadForm.type === 'video'">
                  <b-form-group
                    label="Video ID"
                    label-for="video-id"
                    class="compact-form-group"
                  >
                    <b-form-input
                      v-model="uploadForm.videoId"
                      id="video-id"
                      size="sm"
                      placeholder="Enter video ID..."
                    />
                    <b-form-text class="compact-help-text">
                      Reference an existing video in your TikTok ad account
                    </b-form-text>
                  </b-form-group>
                </div>
              </div>
            </div>

            <!-- Video Processing Options Section -->
            <div
              v-if="uploadForm.type === 'video'"
              class="form-section"
            >
              <div class="section-header">
                <h6 class="mb-0">
                  <feather-icon
                    icon="VideoIcon"
                    size="16"
                    class="mr-2"
                  />
                  Video Processing Options
                </h6>
              </div>
              <div class="section-body">
                <div class="video-options-vertical">
                  <div class="video-switch-group">
                    <div class="switch-row">
                      <b-form-checkbox
                        v-model="uploadForm.flawDetect"
                        switch
                        size="sm"
                        class="video-toggle"
                      >
                        <span class="switch-label">Flaw Detection</span>
                      </b-form-checkbox>
                    </div>
                    <small class="text-muted switch-description">Auto-detect video issues</small>

                    <!-- Auto-Fix as sub-option under Flaw Detection -->
                    <div
                      class="video-switch-group sub-option"
                      :class="{ 'disabled': !uploadForm.flawDetect }"
                    >
                      <div class="switch-row">
                        <b-form-checkbox
                          v-model="uploadForm.autoFixEnabled"
                          switch
                          size="sm"
                          class="video-toggle"
                          :disabled="!uploadForm.flawDetect"
                        >
                          <span class="switch-label">Auto-Fix</span>
                        </b-form-checkbox>
                      </div>
                      <small class="text-muted switch-description">Auto-fix detected issues (requires Flaw Detection)</small>
                    </div>
                  </div>

                  <div class="video-switch-group">
                    <div class="switch-row">
                      <b-form-checkbox
                        v-model="uploadForm.autoBindEnabled"
                        switch
                        size="sm"
                        class="video-toggle"
                      >
                        <span class="switch-label">Auto-Bind</span>
                      </b-form-checkbox>
                    </div>
                    <small class="text-muted switch-description">Auto-bind to ad account</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pre-Review Information Section -->
            <div class="form-section">
              <div class="section-header">
                <h6 class="mb-0">
                  <feather-icon
                    icon="CheckSquareIcon"
                    size="16"
                    class="mr-2"
                  />
                  Pre-Review Information
                </h6>
              </div>
              <div class="section-body">
                <b-row>
                  <b-col md="6">
                    <b-form-group
                      label="Ad Text"
                      label-for="ad-text"
                      class="compact-form-group"
                      :state="adTextValidationState"
                      :invalid-feedback="adTextInvalidFeedback"
                    >
                      <b-form-textarea
                        v-model="uploadForm.adText"
                        id="ad-text"
                        rows="3"
                        size="sm"
                        placeholder="Enter ad text content..."
                        :state="adTextValidationState"
                      />
                      <small class="form-text text-muted mt-2">
                        Message for your audience (1-100 characters, no emoji). Chinese/Japanese words count as 2 characters.
                      </small>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group
                      label="Landing Page URL"
                      label-for="landing-page-url"
                      class="compact-form-group"
                      :state="landingPageUrlValidationState"
                      :invalid-feedback="landingPageUrlInvalidFeedback"
                    >
                      <b-form-input
                        v-model="uploadForm.landingPageUrl"
                        id="landing-page-url"
                        type="url"
                        size="sm"
                        placeholder="Enter landing page URL..."
                        :state="landingPageUrlValidationState"
                      />
                    </b-form-group>
                    <b-form-group
                      label="Location Codes"
                      label-for="location-codes"
                      class="compact-form-group"
                    >
                      <b-form-tags
                        v-model="uploadForm.locationCodes"
                        id="location-codes"
                        size="sm"
                        placeholder="Add location codes..."
                        separator=" ,;"
                      />
                      <b-form-text class="compact-help-text">
                        Comma, space, or semicolon separated. Use US only for now.
                      </b-form-text>
                    </b-form-group>
                    <div class="compact-switch-group mt-2">
                      <b-form-checkbox
                        v-model="uploadForm.isEcommerce"
                        switch
                        size="sm"
                      >
                        E-commerce Ad
                      </b-form-checkbox>
                      <small class="text-muted d-block">Whether the creatives are intended for use in E-commerce ads, including GMV Max Campaigns and Catalog Ads.</small>
                    </div>
                  </b-col>
                </b-row>
              </div>
            </div>
          </b-card>
        </b-col>

        <!-- Right Column -->
        <b-col lg="4">
          <!-- File Preview Card -->
          <b-card
            v-if="filePreview"
            class="mb-3"
          >
            <b-card-header class="compact-header">
              <h6 class="mb-0">
                <feather-icon
                  icon="EyeIcon"
                  size="16"
                  class="mr-2"
                />
                Preview
              </h6>
            </b-card-header>
            <b-card-body class="compact-body text-center">
              <div class="file-preview-compact">
                <div
                  v-if="uploadForm.type === 'image'"
                  class="image-preview"
                >
                  <b-img
                    :src="filePreview"
                    fluid
                    thumbnail
                    class="preview-media"
                  />
                </div>
                <div
                  v-else
                  class="video-preview"
                >
                  <video
                    controls
                    class="preview-media"
                  >
                    <source
                      :src="filePreview"
                      :type="uploadForm.file.type"
                    >
                  </video>
                </div>
                <div class="mt-2">
                  <small class="text-muted">
                    {{ uploadForm.file.name }}
                  </small>
                  <br>
                  <small class="text-muted">
                    {{ formatFileSize(uploadForm.file.size) }}
                  </small>
                </div>
              </div>
            </b-card-body>
          </b-card>

          <!-- Upload Progress Card -->
          <b-card
            v-if="hasActiveUpload"
            class="mb-3"
          >
            <b-card-header class="compact-header">
              <h6 class="mb-0">
                <feather-icon
                  icon="UploadCloudIcon"
                  size="16"
                  class="mr-2"
                />
                Upload Progress
              </h6>
            </b-card-header>
            <b-card-body class="compact-body">
              <b-progress
                :value="uploadProgress"
                :max="100"
                show-progress
                animated
                striped
                variant="primary"
                class="compact-progress"
              />
              <div class="text-center mt-2">
                <small class="text-muted">{{ uploadStatusText }}</small>
              </div>
            </b-card-body>
          </b-card>

          <!-- Upload Result Card -->
          <b-card
            v-if="currentUpload"
            class="mb-3"
          >
            <b-card-header class="compact-header bg-success text-white">
              <h6 class="mb-0">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="16"
                  class="mr-2"
                />
                Upload Result
              </h6>
            </b-card-header>
            <b-card-body class="compact-body">
              <div class="upload-result-compact">
                <div class="result-item">
                  <strong>ID:</strong> {{ currentUpload.id }}
                </div>
                <div class="result-item">
                  <strong>Status:</strong>
                  <b-badge :variant="getStatusVariant(currentUpload.status)">
                    {{ currentUpload.status }}
                  </b-badge>
                </div>
                <div class="result-item">
                  <strong>Type:</strong> {{ uploadForm.type ? uploadForm.type.charAt(0).toUpperCase() + uploadForm.type.slice(1) : 'Image' }}
                </div>
                <div class="result-item">
                  <strong>Created:</strong> {{ formatDate(currentUpload.created_at) }}
                </div>
                <div
                  v-if="currentUpload.preview_url"
                  class="result-item"
                >
                  <b-link
                    :href="currentUpload.preview_url"
                    target="_blank"
                    class="btn btn-outline-primary btn-sm"
                  >
                    <feather-icon
                      icon="ExternalLinkIcon"
                      size="14"
                      class="mr-1"
                    />
                    View Preview
                  </b-link>
                </div>
              </div>
              <div class="mt-3">
                <b-button
                  variant="primary"
                  size="sm"
                  block
                  accesskey="v"
                  title="View Creative (Alt+V)"
                  :disabled="isLoading"
                  @click="navigateToCreative(currentUpload.id)"
                >
                  <b-spinner
                    v-if="isLoading"
                    small
                    class="mr-1"
                  />
                  <feather-icon
                    icon="ImageIcon"
                    size="14"
                    class="mr-1"
                  />
                  View Creative
                </b-button>
              </div>
            </b-card-body>
          </b-card>

          <!-- Success Result Card -->
          <b-card
            v-if="uploadSuccessResult"
            class="upload-success-card mb-3"
            :border-variant="isUploadSuccessful ? 'success' : 'danger'"
          >
            <b-card-body>
              <div class="d-flex align-items-center mb-3">
                <feather-icon
                  :icon="isUploadSuccessful ? 'CheckCircleIcon' : 'AlertCircleIcon'"
                  size="24"
                  :class="isUploadSuccessful ? 'text-success' : 'text-danger'"
                  class="mr-2"
                />
                <h5 class="mb-0">
                  {{ isUploadSuccessful ? 'Upload Successful!' : 'Upload Error' }}
                </h5>
              </div>

              <p
                v-if="isUploadSuccessful"
                 class="mb-3"
              >
                Your {{ uploadForm.type }} has been uploaded and submitted for pre-review.
              </p>

              <div
                v-if="uploadSuccessResult.result && isUploadSuccessful"
                class="mb-3"
              >
                <small class="text-muted">
                  Task ID: {{ uploadSuccessResult.result.task_id }}<br>
                  Creative ID: {{ uploadSuccessResult.result.creative_ids[0] }}<br>
                  Status: <b-badge :variant="getStatusVariant(uploadSuccessResult.result.task_status)">
                    {{ uploadSuccessResult.result.task_status }}
                  </b-badge>
                </small>
              </div>

              <div
                v-if="!isUploadSuccessful && uploadSuccessResult.result"
                class="mb-3"
              >
                <!-- Display error message for simple errors like file too large -->
                <div v-if="uploadSuccessResult.result.error_message">
                  <div class="text-danger">
                    <strong>{{ uploadSuccessResult.result.error_message }}</strong>
                  </div>
                  <div
v-if="uploadSuccessResult.result.details"
class="mt-2"
>
                    <small class="text-muted">{{ uploadSuccessResult.result.details }}</small>
                  </div>
                  <div
v-if="uploadSuccessResult.result.file_info && uploadSuccessResult.result.file_info.suggestion"
class="mt-2"
>
                    <small class="text-info">
                      <b-icon-info-circle class="mr-1" />
                      <strong>Tip:</strong> {{ uploadSuccessResult.result.file_info.suggestion }}
                    </small>
                  </div>
                </div>

                <!-- Display upload_results errors if available -->
                <div v-else-if="uploadSuccessResult.result.upload_results">
                  <small class="text-danger">
                    <template v-for="(fileResult, fileName) in uploadSuccessResult.result.upload_results">
                      <div :key="fileName">
                        <strong>{{ fileResult.filename || fileName }}:</strong> {{ fileResult.error || 'Failed' }}
                      </div>
                    </template>
                  </small>
                </div>
              </div>

              <div class="d-flex justify-content-between align-items-center mt-3">

                <div class="d-flex gap-2">
<!--                  <b-button-->
<!--                    v-if="isUploadSuccessful && uploadSuccessResult.result && uploadSuccessResult.result.creative_ids && uploadSuccessResult.result.creative_ids[0]"-->
<!--                    variant="outline-primary"-->
<!--                    size="sm"-->
<!--                    @click="navigateToCreative(uploadSuccessResult.result.creative_ids[0])"-->
<!--                  >-->
<!--                    <feather-icon-->
<!--                      icon="ImageIcon"-->
<!--                      size="14"-->
<!--                      class="mr-1"-->
<!--                    />-->
<!--                    View Creative-->
<!--                  </b-button>-->

                  <b-button
                    v-if="isUploadSuccessful && uploadSuccessResult.result && uploadSuccessResult.result.task_id"
                    variant="outline-success"
                    size="sm"
                    class="ml-2"
                    @click="navigateToTask(uploadSuccessResult.result.task_id)"
                  >
                    <feather-icon
                      icon="ListIcon"
                      size="14"
                      class="mr-1"
                    />
                    View Task
                  </b-button>
                </div>
              </div>
            </b-card-body>
          </b-card>

          <!-- Upload Action Card -->
          <b-card class="upload-action-card">
            <b-card-body class="compact-body">
              <b-button
                ref="uploadButton"
                type="submit"
                variant="primary"
                size="medium"
                block
                accesskey="u"
                title="Upload Creative (Alt+U)"
                class="btn-submit"
                :disabled="!isFormValid || isLoading"
              >
                <b-spinner
                  v-if="isLoading"
                  small
                  class="mr-2"
                />
                <feather-icon
                  icon="UploadIcon"
                  size="16"
                  class="mr-2"
                />
                Upload {{ uploadForm.type }}
              </b-button>
              <div class="text-center mt-2">
                <small class="text-muted">
                  Press Alt+U to upload
                </small>
              </div>

              <div class="text-center mt-2">
                <b-button
                  variant="outline-danger"
                  size="sm"
                  accesskey="r"
                  title="Reset Form (Alt+R)"
                  :disabled="isLoading"
                  @click="handleResetForm"
                >
                  <feather-icon
                    icon="RefreshCwIcon"
                    size="16"
                    class="mr-1"
                  />
                  Reset
                </b-button>
              </div>

            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-form>

    <!-- Upload Progress Modal -->
    <b-modal
      v-model="showUploadModal"
      title="Uploading Creative"
      centered
      no-close-on-backdrop
      no-close-on-esc
      hide-footer
      :busy="isUploading"
    >
      <div class="upload-modal-content">
        <div class="text-center mb-3">
          <feather-icon
            icon="UploadCloudIcon"
            size="48"
            class="text-primary mb-3"
          />
          <h5>{{ uploadModalTitle }}</h5>
          <p class="text-muted mb-3">
            {{ uploadModalMessage }}
          </p>
        </div>

        <b-progress
          :value="uploadProgress"
          :max="100"
          height="25px"
          animated
          striped
          show-progress
          :variant="uploadProgressVariant"
        />

        <div class="mt-3 text-center">
          <small class="text-muted">
            <span v-if="uploadProgress < 100">
              {{ formatUploadSpeed() }} • {{ estimatedTimeRemaining() }}
            </span>
            <span v-else>
              Processing...
            </span>
          </small>
        </div>

        <div
          v-if="uploadedBytes && totalBytes"
          class="mt-2 text-center"
        >
          <small class="text-muted">
            {{ formatFileSize(uploadedBytes) }} / {{ formatFileSize(totalBytes) }}
          </small>
        </div>

        <div class="mt-4 text-center">
          <b-button
            variant="outline-danger"
            size="sm"
            @click="cancelUpload"
          >
            <feather-icon
              icon="XIcon"
              size="14"
              class="mr-1"
            />
            Cancel Upload
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BForm,
  BFormGroup,
  BFormRadioGroup,
  BFormSelect,
  BFormSelectOption,
  BFormFile,
  BFormInput,
  BFormTextarea,
  BFormTags,
  BFormCheckbox,
  BFormText,
  BRow,
  BCol,
  BImg,
  BProgress,
  BSpinner,
  BLink,
  BBadge,
  BModal,
  BIconInfoCircle,
  BIconBookmark,
} from 'bootstrap-vue'
import { createNamespacedHelpers } from 'vuex'
import { toastification } from '@core/mixins/toast'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import CreativeService from '@/services/CreativeService'
import axiosLib from 'axios'

const { mapGetters: mapCreativeGetters, mapActions: mapCreativeActions } = createNamespacedHelpers('creativeUpload')
const { mapGetters: mapAdsGetters, mapActions: mapAdsActions } = createNamespacedHelpers('adsAccount')

export default {
  name: 'PrePreviewUpload',

  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BForm,
    BFormGroup,
    BFormRadioGroup,
    BFormSelect,
    BFormSelectOption,
    BFormFile,
    BFormInput,
    BFormTextarea,
    BFormTags,
    BFormCheckbox,
    BFormText,
    BRow,
    BCol,
    BImg,
    BProgress,
    BSpinner,
    BLink,
    BBadge,
    BModal,
    BIconInfoCircle,
    BIconBookmark,
    FeatherIcon,
  },

  mixins: [toastification],

  data() {
    return {
      filePreview: null,
      videoOptionsExpanded: true,
      advertiserInputMode: 'manual', // 'manual' or 'select'
      uploadSuccessResult: null, // Store the upload result for navigation
      showPreReviewValidation: false, // Track if validation should be shown
      savingDefault: false, // Track saving default advertiser state
      // Upload modal properties
      showUploadModal: false,
      isUploading: false,
      uploadedBytes: 0,
      totalBytes: 0,
      uploadStartTime: null,
      uploadCancelSource: null,
      creativeTypeOptions: [
        { text: 'Video', value: 'video' },
        { text: 'Image', value: 'image' },
      ],
      advertiserInputModeOptions: [
        { text: 'Manual', value: 'manual' },
        { text: 'Select', value: 'select' },
      ],
    }
  },

  computed: {
    ...mapCreativeGetters([
      'uploadForm',
      'isFormValid',
      'isFileValid',
      'fileValidationErrors',
      'availableUploadMethods',
      'uploadProgress',
      'hasActiveUpload',
      'uploadStatusText',
      'currentUpload',
      'isPreReviewFormValid',
      'loading',
    ]),
    ...mapAdsGetters([
      'listAccount',
    ]),

    isLoading() {
      return this.loading
    },

    fileAcceptTypes() {
      if (this.uploadForm.type === 'video') {
        return '.mp4,.mov,.mpeg,.avi'
      }
      return '.jpg,.jpeg,.png'
    },

    urlValidation() {
      if (!this.uploadForm.url) return null
      try {
        // eslint-disable-next-line no-new
        new URL(this.uploadForm.url)
        return true
      } catch {
        return false
      }
    },

    canSubmitPreReview() {
      return this.currentUpload && this.isPreReviewFormValid
    },

    advertiserOptions() {
      const accounts = this.listAccount?.content || []
      return accounts.map(account => {
        // eslint-disable-next-line no-underscore-dangle
        const accountId = account._id
        return {
          value: accountId,
          text: `${account.displayName || account.name || 'Ad Account'} (ID: ${accountId})`,
        }
      })
    },

    isUploadSuccessful() {
      if (!this.uploadSuccessResult || !this.uploadSuccessResult.result) {
        return false
      }
      const { result } = this.uploadSuccessResult
      // Check if any files were successfully uploaded
      return result.successful_uploads > 0
    },

    adTextValidationState() {
      // Show validation state only if user has attempted to submit or both fields are empty
      if (!this.showPreReviewValidation) return null

      const hasAdText = !!(this.uploadForm.adText && this.uploadForm.adText.trim())
      const hasLandingPageUrl = !!(this.uploadForm.landingPageUrl && this.uploadForm.landingPageUrl.trim())

      // If either field has content, this field is valid
      if (hasAdText || hasLandingPageUrl) return true

      // If both are empty, show invalid state
      return false
    },

    landingPageUrlValidationState() {
      // Show validation state only if user has attempted to submit or both fields are empty
      if (!this.showPreReviewValidation) return null

      const hasAdText = !!(this.uploadForm.adText && this.uploadForm.adText.trim())
      const hasLandingPageUrl = !!(this.uploadForm.landingPageUrl && this.uploadForm.landingPageUrl.trim())

      // If either field has content, this field is valid
      if (hasAdText || hasLandingPageUrl) return true

      // If both are empty, show invalid state
      return false
    },

    adTextInvalidFeedback() {
      return 'Either Ad Text or Landing Page URL must be provided'
    },

    landingPageUrlInvalidFeedback() {
      return 'Either Ad Text or Landing Page URL must be provided'
    },

    uploadFailureMessage() {
      if (!this.uploadSuccessResult || !this.uploadSuccessResult.result) {
        return 'Upload failed. Please try again.'
      }
      const { result } = this.uploadSuccessResult
      if (result.message) {
        return result.message
      }
      if (result.failed_uploads > 0) {
        return `Failed to upload ${result.failed_uploads} file(s). See details below.`
      }
      return 'Upload failed. Please try again.'
    },

    uploadModalTitle() {
      if (this.uploadProgress === 100) {
        return 'Processing Creative...'
      }
      const fileType = this.uploadForm.type === 'video' ? 'Video' : 'Image'
      return `Uploading ${fileType}...`
    },

    uploadModalMessage() {
      if (this.uploadProgress === 100) {
        return 'Please wait while we process your creative for pre-review.'
      }
      const fileName = this.uploadForm.file?.name || 'creative'
      return `Uploading "${fileName}"`
    },

    uploadProgressVariant() {
      if (this.uploadProgress === 100) {
        return 'success'
      }
      return 'primary'
    },
  },

  watch: {
    'uploadForm.autoFixEnabled': function autoFixEnabledWatcher(enabled) {
      if (enabled && !this.uploadForm.flawDetect) {
        this.updateForm({ flawDetect: true })
      }
    },
  },

  async mounted() {
    this.resetForm()
    // Ensure location codes is always set to US
    this.updateForm({ locationCodes: ['US'] })

    // Retrieve advertiser ID from localStorage (check both sources like TaskLog)
    let savedAdvertiserId = localStorage.getItem('defaultPreviewAdvertiserId')

    // If not found, check userData as fallback
    if (!savedAdvertiserId) {
      const userData = localStorage.getItem('userData')
      if (userData) {
        try {
          const parsedData = JSON.parse(userData)
          if (parsedData?.defaultPreviewAdvertiserId) {
            savedAdvertiserId = parsedData.defaultPreviewAdvertiserId
          }
        } catch (e) {
          // Handle JSON parse error silently
        }
      }
    }

    if (savedAdvertiserId) {
      this.updateForm({ advertiserId: savedAdvertiserId })
    }

    // Pre-fetch ad accounts in the background (non-blocking)
    this.getAccountList({ page: 1, size: 100 }).catch(() => {
    })
  },

  beforeDestroy() {
    this.cleanupFilePreview()
  },

  methods: {
    ...mapCreativeActions([
      'updateForm',
      'resetForm',
      'validateFile',
      'uploadVideoCreative',
      'uploadImageCreative',
      'submitPreReview',
      'setUploadProgress',
      'setCurrentUpload',
    ]),
    ...mapAdsActions([
      'getAccountList',
    ]),

    handleResetForm() {
      // Clear upload success/error result card
      this.uploadSuccessResult = null

      // Clear file preview
      this.cleanupFilePreview()

      // Clear validation state
      this.showPreReviewValidation = false

      // Call the store's resetForm action
      this.resetForm()

      // Reset location codes to US after form reset
      this.updateForm({ locationCodes: ['US'] })
    },

    handleTypeChange() {
      // Reset file and validation when type changes
      this.updateForm({
        file: null,
        url: '',
        fileId: '',
        videoId: '',
        uploadMethod: 'UPLOAD_BY_FILE',
      })
      this.cleanupFilePreview()
    },

    handleMethodChange() {
      // Reset inputs when method changes
      this.updateForm({
        file: null,
        url: '',
        fileId: '',
        videoId: '',
      })
      this.cleanupFilePreview()
    },

    handleAdvertiserChange(value) {
      // Save to localStorage when dropdown selection changes
      if (value) {
        localStorage.setItem('defaultPreviewAdvertiserId', value)

        // Also save to userData for consistency with TaskLog
        const userData = localStorage.getItem('userData')
        if (userData) {
          try {
            const parsedData = JSON.parse(userData)
            parsedData.defaultPreviewAdvertiserId = value
            localStorage.setItem('userData', JSON.stringify(parsedData))
          } catch (e) {
            // Handle JSON parse error silently
          }
        }
      }
    },

    async handleInputModeChange(mode) {
      // Clear the current advertiser ID when switching modes
      this.updateForm({ advertiserId: null })

      // If switching to select mode, fetch the ad accounts
      if (mode === 'select') {
        try {
          // Fetch ad accounts if not already loaded
          if (!this.listAccount || !this.listAccount.content || this.listAccount.content.length === 0) {
            await this.getAccountList({ page: 1, size: 100 })
          }

          // Check if we have accounts after fetching
          if (this.advertiserOptions.length === 0) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Info',
                icon: 'InfoIcon',
                text: 'No existing ad accounts found. You can manually enter an advertising ID.',
                variant: 'info',
              },
            })
            // Switch back to manual mode if no accounts
            this.advertiserInputMode = 'manual'
          }
        } catch (error) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertTriangleIcon',
              text: 'Failed to fetch ad accounts. Please try again or enter manually.',
              variant: 'danger',
            },
          })
          // Switch back to manual mode on error
          this.advertiserInputMode = 'manual'
        }
      }
    },

    handleAdvertiserManualInput(value) {
      // Handle manual input changes
      // Remove any non-numeric characters if needed (advertising IDs are usually numeric)
      const cleanValue = value.replace(/[^0-9]/g, '')
      if (cleanValue !== value) {
        this.updateForm({ advertiserId: cleanValue })
      }
      // Save to localStorage
      if (cleanValue) {
        localStorage.setItem('defaultPreviewAdvertiserId', cleanValue)

        // Also save to userData for consistency with TaskLog
        const userData = localStorage.getItem('userData')
        if (userData) {
          try {
            const parsedData = JSON.parse(userData)
            parsedData.defaultPreviewAdvertiserId = cleanValue
            localStorage.setItem('userData', JSON.stringify(parsedData))
          } catch (e) {
            // Handle JSON parse error silently
          }
        }
      }
    },

    async saveAsDefaultAdvertiser() {
      this.savingDefault = true
      try {
        const { advertiserId } = this.uploadForm

        // Save to localStorage for immediate persistence
        localStorage.setItem('defaultPreviewAdvertiserId', advertiserId)

        // Also save to userData for consistency with TaskLog
        const userData = localStorage.getItem('userData')
        if (userData) {
          try {
            const parsedData = JSON.parse(userData)
            parsedData.defaultPreviewAdvertiserId = advertiserId
            localStorage.setItem('userData', JSON.stringify(parsedData))
          } catch (e) {
            // Handle JSON parse error silently
          }
        }

        // Show success message
        this.toastSuccess(`Advertiser ${advertiserId} saved as default`)
      } catch (error) {
        this.toastFailure('Failed to save default advertiser. Please try again.')
      } finally {
        this.savingDefault = false
      }
    },

    async handleFileInput(file) {
      if (!file) {
        this.cleanupFilePreview()
        return
      }

      // Validate file (await the Promise)
      const validation = await this.validateFile({ file, type: this.uploadForm.type })

      // Check if validation exists and has the expected structure
      if (!validation || !validation.isValid) {
        const errorMessage = (validation && validation.errors && Array.isArray(validation.errors))
          ? validation.errors.join(', ')
          : 'Invalid file format or size'
        this.toastFailure(errorMessage)
        this.updateForm({ file: null })
        return
      }
      // Create preview
      this.createFilePreview(file)
      this.updateForm({ file })
    },

    createFilePreview(file) {
      this.cleanupFilePreview()

      if (file) {
        this.filePreview = URL.createObjectURL(file)
      }
    },

    cleanupFilePreview() {
      if (this.filePreview) {
        URL.revokeObjectURL(this.filePreview)
        this.filePreview = null
      }
    },

    async handleSubmit() {
      try {
        // Reset upload success result to hide the banner
        this.uploadSuccessResult = null

        // Validate that either Ad Text or Landing Page URL is provided
        const hasAdText = !!(this.uploadForm.adText && this.uploadForm.adText.trim())
        const hasLandingPageUrl = !!(this.uploadForm.landingPageUrl && this.uploadForm.landingPageUrl.trim())

        if (!hasAdText && !hasLandingPageUrl) {
          this.showPreReviewValidation = true
          this.toastFailure('Please provide either Ad Text or Landing Page URL to proceed with pre-review.')
          return
        }

        // Clear validation state if form is valid
        this.showPreReviewValidation = false

        // Check if we have pre-review information
        const hasPreReviewInfo = hasAdText || hasLandingPageUrl

        // Check if pre-review info is available

        if (hasPreReviewInfo) {
          // Use combined upload-with-review endpoint
          // Use combined upload-with-review endpoint
          const result = await this.uploadWithPreReview()

          // Show success with navigation options
          this.showUploadSuccessResult(result)
        } else {
          // Use regular upload endpoints
          // Use regular upload endpoints
          let uploadResult
          if (this.uploadForm.type === 'video') {
            uploadResult = await this.uploadVideoCreative()
          } else {
            uploadResult = await this.uploadImageCreative()
          }
          // Only show success toast if upload actually succeeded
          if (uploadResult) {
            this.toastSuccess(`${this.uploadForm.type} uploaded successfully!`)
          }
        }
      } catch (error) {
        // Upload failed
        // Close the upload modal first
        this.showUploadModal = false
        this.isUploading = false
        this.uploadCancelSource = null

        // Handle network errors (no response)
        if (error.message === 'Network Error' || (!error.response && !error.status)) {
          const errorMessage = 'Network error: Cannot connect to server. Please check if the backend is running.'

          // Show error in the result card (no toast needed)
          this.showUploadSuccessResult({
            success: false,
            error: errorMessage,
            result: {
              error_message: errorMessage,
              status: 'failed',
              details: 'Unable to connect to the server. This could be due to network issues or the backend service being unavailable.',
            },
          })
          return
        }

        // Get status from error.response.status or error.status
        const status = error.response?.status || error.status

        // Handle specific error cases
        if (status === 413) {
          // Handle file too large error - be specific based on file type
          const isVideo = this.uploadForm.type === 'video'
          const maxSize = isVideo ? '500MB' : '50MB'
          const fileType = isVideo ? 'video' : 'image'

          const errorMessage = `File too large! Your ${fileType} exceeds the ${maxSize} limit.`
          const details = isVideo
            ? 'Videos must be under 500MB. Try compressing your video or reducing its resolution/quality.'
            : 'Images must be under 50MB. Try resizing your image or using a lower quality/compression setting.'

          this.toastFailure(errorMessage)

          // Show error in the result card
          this.showUploadSuccessResult({
            success: false,
            error: errorMessage,
            result: {
              error_message: errorMessage,
              status: 'failed',
              details,
              file_info: {
                type: fileType,
                max_size: maxSize,
                suggestion: isVideo
                  ? 'Consider using video compression tools or uploading a shorter video clip.'
                  : 'Consider using image optimization tools or reducing the image dimensions.',
              },
            },
          })
        } else if (status === 404) {
          const errorMessage = 'Upload endpoint not found. Please ensure the backend API is properly configured.'
          this.toastFailure(errorMessage)
          this.showUploadSuccessResult({
            success: false,
            error: errorMessage,
            result: {
              error_message: errorMessage,
              status: 'failed',
            },
          })
        } else if (status === 401) {
          const errorMessage = 'Authentication failed. Please check your credentials.'
          this.toastFailure(errorMessage)
          this.showUploadSuccessResult({
            success: false,
            error: errorMessage,
            result: {
              error_message: errorMessage,
              status: 'failed',
            },
          })
        } else if (status === 409) {
          // Handle duplicate filename error
          const errorData = error.response?.data || error.data || error
          let errorMessage = 'Duplicate filename detected'
          let suggestion = ''

          if (errorData?.details?.suggestion) {
            suggestion = errorData.details.suggestion
          }

          if (errorData?.message) {
            errorMessage = errorData.message
          }

          // Show error with suggestion
          const fullErrorMessage = suggestion
            ? `${errorMessage}. ${suggestion}`
            : `${errorMessage}. Please rename your file and try again.`

          this.toastFailure(fullErrorMessage)
          this.showUploadSuccessResult({
            success: false,
            error: fullErrorMessage,
            result: {
              error_message: errorMessage,
              status: 'failed',
              details: suggestion || 'Please rename your file and try again.',
            },
          })
        } else if (status >= 500) {
          const errorMessage = 'Server error occurred. Please try again later.'
          this.toastFailure(errorMessage)
          this.showUploadSuccessResult({
            success: false,
            error: errorMessage,
            result: {
              error_message: errorMessage,
              status: 'failed',
              details: `Server returned status code ${status}`,
            },
          })
        } else {
          // Extract meaningful error message from backend response
          let errorMessage = 'Unknown error'

          // Try multiple ways to get the error data
          const errorData = error.response?.data || error.data || error

          if (errorData?.message) {
            errorMessage = errorData.message
            // Extract the core TikTok API error if it exists
            const tikTokErrorMatch = errorMessage.match(/TikTok API Error: (.+)\.?$/)
            if (tikTokErrorMatch) {
              // eslint-disable-next-line prefer-destructuring
              errorMessage = tikTokErrorMatch[1]
            }
          } else if (errorData?.code) {
            errorMessage = errorData.code
          } else if (error.message) {
            errorMessage = error.message
          }

          this.toastFailure(`Upload failed: ${errorMessage}`)

          // Show error in the result card
          this.showUploadSuccessResult({
            success: false,
            error: errorMessage,
            result: {
              error_message: errorMessage,
              status: 'failed',
            },
          })
        }
      }
    },

    async uploadWithPreReview() {
      // Show upload modal for file uploads
      if (this.uploadForm.uploadMethod === 'UPLOAD_BY_FILE' && this.uploadForm.file) {
        this.showUploadModal = true
        this.isUploading = true
        this.uploadedBytes = 0
        this.totalBytes = this.uploadForm.file.size
        this.uploadStartTime = Date.now()
        // Create cancel token
        this.uploadCancelSource = axiosLib.CancelToken.source()
      }

      try {
        // Ensure location codes are always present
        const locationCodes = this.uploadForm.locationCodes && this.uploadForm.locationCodes.length > 0
          ? this.uploadForm.locationCodes
          : ['US']

        const params = {
          type: this.uploadForm.type,
          advertiser_id: this.uploadForm.advertiserId,
          upload_type: this.uploadForm.uploadMethod,
          ad_text: this.uploadForm.adText,
          landing_page_url: this.uploadForm.landingPageUrl,
          location_codes: locationCodes,
          is_ecommerce: this.uploadForm.isEcommerce,
        }

        // Prepare location codes for API

        // Add file or URL based on upload method
        if (this.uploadForm.uploadMethod === 'UPLOAD_BY_FILE') {
          params.file = this.uploadForm.file
        } else if (this.uploadForm.uploadMethod === 'UPLOAD_BY_URL') {
          params.url = this.uploadForm.url
        }

        // Add video-specific options
        if (this.uploadForm.type === 'video') {
          params.flaw_detect = this.uploadForm.flawDetect
          params.auto_fix_enabled = this.uploadForm.autoFixEnabled
          params.auto_bind_enabled = this.uploadForm.autoBindEnabled
        }

        // Call uploadWithPreReview with progress tracking and cancel support

        const response = await CreativeService.uploadWithPreReview(params, {
          onUploadProgress: progressEvent => {
            if (this.uploadForm.uploadMethod === 'UPLOAD_BY_FILE') {
              this.uploadedBytes = progressEvent.loaded
              this.totalBytes = progressEvent.total
            }
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            this.setUploadProgress(progress)
          },
          cancelToken: this.uploadCancelSource ? this.uploadCancelSource.token : undefined,
        })

        // Store the upload result
        if (response.data?.data?.upload_result) {
          this.setCurrentUpload(response.data.data.upload_result)
        }

        return response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Upload error:', error)
        if (axiosLib.isCancel(error)) {
          this.toastInfo('Upload cancelled')
          throw new Error('Upload cancelled')
        }
        throw error
      } finally {
        // Hide modal
        this.showUploadModal = false
        this.isUploading = false
        this.uploadCancelSource = null
      }
    },

    validatePreReviewForm() {
      const errors = []
      const form = this.uploadForm

      if (!form.adText || form.adText.trim().length === 0) {
        errors.push('Ad text is required')
      } else if (form.adText.trim().length < 10) {
        errors.push('Ad text must be at least 10 characters long')
      } else if (form.adText.trim().length > 500) {
        errors.push('Ad text must be less than 500 characters')
      }

      if (!form.landingPageUrl || form.landingPageUrl.trim().length === 0) {
        errors.push('Landing page URL is required')
      } else {
        try {
          const url = new URL(form.landingPageUrl)
          // Valid URL, no action needed
          if (!url) {
            errors.push('Landing page URL must be a valid URL')
          }
        } catch {
          errors.push('Landing page URL must be a valid URL')
        }
      }

      if (!form.locationCodes || form.locationCodes.length === 0) {
        errors.push('At least one location code is required')
      } else if (form.locationCodes.some(code => !code || code.trim().length === 0)) {
        errors.push('All location codes must be valid')
      }

      return errors
    },

    showUploadSuccessResult(result) {
      // Store the result for display in the success card
      this.uploadSuccessResult = result

      // Only show success toast if it's actually successful
      if (result && result.success !== false) {
        // Also show a toast notification
        this.toastSuccess(`${this.uploadForm.type} uploaded and submitted for pre-review successfully!`)
      }

      // Don't reset form immediately - let user navigate first
    },

    navigateToCreative(creativeId) {
      // Navigate to creatives list with the creative ID as a query param or hash
      // Since there's no detail route, we'll go to the list and could potentially highlight/filter
      this.$router.push({
        name: 'pre-preview-creatives',
        query: { creativeId },
      })
    },

    navigateToTask(taskId) {
      // Navigate to tasks list with the task ID and advertiser ID as query params
      const advertiserId = this.uploadForm.advertiserId || this.uploadSuccessResult?.result?.advertiser_id
      this.$router.push({
        name: 'pre-preview-tasks',
        query: {
          taskId,
          advertiserId, // Pass advertiser ID so TaskLog can fetch the right tasks
        },
      })
    },

    goBack() {
      this.$router.push({ name: 'pre-preview' })
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleString()
    },

    getStatusVariant(status) {
      switch (status?.toLowerCase()) {
        case 'completed':
        case 'success':
          return 'success'
        case 'processing':
        case 'pending':
          return 'warning'
        case 'failed':
        case 'error':
          return 'danger'
        default:
          return 'secondary'
      }
    },

    focusUploadButton() {
      if (this.$refs.uploadButton) {
        this.$refs.uploadButton.focus()
      }
    },

    formatUploadSpeed() {
      if (!this.uploadStartTime || !this.uploadedBytes) {
        return 'Calculating...'
      }
      const elapsedSeconds = (Date.now() - this.uploadStartTime) / 1000
      const bytesPerSecond = this.uploadedBytes / elapsedSeconds
      return `${this.formatFileSize(Math.round(bytesPerSecond))}/s`
    },

    estimatedTimeRemaining() {
      if (!this.uploadStartTime || !this.uploadedBytes || !this.totalBytes) {
        return 'Calculating...'
      }
      const elapsedSeconds = (Date.now() - this.uploadStartTime) / 1000
      const bytesPerSecond = this.uploadedBytes / elapsedSeconds
      const remainingBytes = this.totalBytes - this.uploadedBytes
      const remainingSeconds = Math.ceil(remainingBytes / bytesPerSecond)

      if (remainingSeconds < 60) {
        return `${remainingSeconds}s remaining`
      }
      const minutes = Math.floor(remainingSeconds / 60)
      const seconds = remainingSeconds % 60
      return `${minutes}m ${seconds}s remaining`
    },

    cancelUpload() {
      if (this.uploadCancelSource) {
        this.uploadCancelSource.cancel('Upload cancelled by user')
        this.uploadCancelSource = null
      }
      this.showUploadModal = false
      this.isUploading = false
      this.uploadedBytes = 0
      this.totalBytes = 0
      this.uploadStartTime = null
      this.setUploadProgress(0)
      this.toastInfo('Upload cancelled')
    },

    toastSuccess(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Success',
          icon: 'CheckIcon',
          text: message,
          variant: 'success',
        },
      })
    },

    toastInfo(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Info',
          icon: 'InfoIcon',
          text: message,
          variant: 'info',
        },
      })
    },

    toastFailure(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Error',
          icon: 'AlertTriangleIcon',
          text: message,
          variant: 'danger',
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './upload.scss';
</style>
